import type { HTMLAttributes, ReactNode } from "react";
import { AlertTriangle, Info, OctagonAlert, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export type AlertVariant = "info" | "warning" | "danger" | "success";

const styles: Record<AlertVariant, string> = {
  info: "border-accent/30 bg-accent/10 text-fg [&_svg]:text-accent",
  warning: "border-warning/30 bg-warning/10 text-fg [&_svg]:text-warning",
  danger: "border-danger/30 bg-danger/10 text-fg [&_svg]:text-danger",
  success: "border-success/30 bg-success/10 text-fg [&_svg]:text-success",
};
const icons: Record<AlertVariant, ReactNode> = {
  info: <Info />,
  warning: <AlertTriangle />,
  danger: <OctagonAlert />,
  success: <CheckCircle2 />,
};

export type AlertProps = HTMLAttributes<HTMLDivElement> & { variant?: AlertVariant; title?: ReactNode; icon?: ReactNode | null };

export function Alert({ variant = "info", title, icon, className, children, ...props }: AlertProps) {
  return (
    <div
      role={variant === "danger" ? "alert" : "status"}
      className={cn("flex gap-3 rounded-md border px-4 py-3 text-sm [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0", styles[variant], className)}
      {...props}
    >
      {icon === null ? null : (icon ?? icons[variant])}
      <div className="flex-1 space-y-0.5">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className="text-fg-muted">{children}</div>}
      </div>
    </div>
  );
}
